import React, { useCallback, useMemo, useState } from 'react';
import { Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import { ReactComponent as BackIcon } from '@repo/common/assets/icons/16px/icon_16px_arrow-left.svg';
import { ReactComponent as HelpIcon } from '@repo/common/assets/icons/20px/icon_20px_help.svg';
import {
  HELP,
  chapterStatus,
  kitCandidates,
  kitStatus,
  kstClock,
  kstDue,
  matesOf,
  retroStatus,
  workedTogether,
} from '../../components/TwoDays/model/completeProject';
import { usePeerReviewDraft } from '../../components/TwoDays/model/useCompleteProject';
import { useWorkboardCtx } from '../../components/TwoDays/model/useWorkboard';
import { HubView, LockedView, submitSummary } from '../../components/TwoDays/completeProject/Hub';
import {
  ChapterNav,
  KitChapter,
  MateChapter,
  RetroChapter,
} from '../../components/TwoDays/completeProject/Chapters';
import { AlertDialog, Sheet } from '../../components/TwoDays/completeProject/ui';
import TdButton from '../../components/TwoDays/Button';
import SuccessView from '../../components/TwoDays/completeProject/Success';
import { postCompleteProjectFeedback } from '../../api/twoDays/board.api';

/** Complete project(동료 평가) 페이지 */

const TwoDaysCompleteProject = () => {
  const ctx = useWorkboardCtx();
  const { data, viewer, base, cp } = ctx;
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [help, setHelp] = useState(false);
  const [confirm, setConfirm] = useState(false);
  const [busy, setBusy] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [errView, setErrView] = useState(null);
  // 제출 직후에만 성공 페이지 — 메뉴로 다시 들어오면 읽기 전용 허브
  const [success, setSuccess] = useState(null);
  const store = usePeerReviewDraft(ctx, cp);
  const { draft, update, editable } = store;

  const mates = useMemo(() => (viewer ? matesOf(data, viewer) : []), [data, viewer]);
  const candidates = useMemo(() => (viewer ? kitCandidates(data, viewer) : []), [data, viewer]);

  const chapter = params.get('chapter');
  const mateIndex = mates.findIndex((m) => m.id === chapter);
  let view = 'hub';
  if (cp.kind === 'locked') view = 'locked';
  else if (cp.kind === 'closed') view = 'hub';
  else if (chapter === 'retro' || chapter === 'kit') view = chapter;
  else if (mateIndex >= 0) view = 'mate';
  const inChapter = view === 'mate' || view === 'retro' || view === 'kit';

  const go = useCallback(
    (key) => {
      setErrView(null);
      setParams((prev) => {
        const next = new URLSearchParams(prev);
        if (key) next.set('chapter', key);
        else next.delete('chapter');
        return next;
      });
      window.scrollTo(0, 0);
    },
    [setParams],
  );
  const toBoard = useCallback(() => navigate(`${base}/tasks/mine`), [navigate, base]);

  if (!cp.eligible) return <Navigate to={`${base}/tasks/mine`} replace />;

  const statusOf = (m) => chapterStatus(draft.chapters[m.id]);
  const retroSt = retroStatus(draft.retro);
  const kitSt = kitStatus(draft.kit);
  const savedText = store.savedAt
    ? `Saved ${kstClock(store.savedAt)}`
    : 'Changes save automatically';

  const setAnswer = (id) => (fn) =>
    update((d) => ({ ...d, chapters: { ...d.chapters, [id]: fn(d.chapters[id] ?? {}) } }));
  const setRetro = (fn) => update((d) => ({ ...d, retro: fn(d.retro) }));
  const setKit = (fn) => update((d) => ({ ...d, kit: fn(d.kit) }));

  const navItems = [
    ...mates.map((m) => ({ key: m.id, label: m.name.split(' ')[0], status: statusOf(m) })),
    { key: 'retro', label: 'Retrospective', status: retroSt },
    { key: 'kit', label: 'Kit items', status: kitSt },
  ];
  const nav = <ChapterNav items={navItems} current={view === 'mate' ? chapter : view} onGo={go} />;

  const doSubmit = async () => {
    setBusy(true);
    setSubmitError('');
    try {
      const late = cp.step === 1;
      const at = new Date().toISOString(); // 실제 제출 시각 (서버 값이 오면 그걸 쓴다)
      await store.submit();
      setConfirm(false);
      go(null);
      setSuccess({ at, late });
    } catch (e) {
      setSubmitError('Couldn’t submit. Check your connection and try again.');
    } finally {
      setBusy(false);
    }
  };

  let body = null;
  if (success) {
    body = (
      <SuccessView
        submittedAt={cp.submittedAt ?? success.at}
        late={cp.submittedAt ? !!cp.late : success.late}
        onViewSubmitted={() => setSuccess(null)}
        onBack={toBoard}
        // 실제 보드는 서버를 거쳐 구글 시트로, 목 보드는 보낸 것으로만 보인다
        onSendFeedback={(text) =>
          ctx.live ? postCompleteProjectFeedback(ctx.slug, text) : Promise.resolve()
        }
      />
    );
  } else if (view === 'locked') {
    body = <LockedView endLabel={kstDue(data.project.endAt)} onBack={toBoard} />;
  } else if (view === 'mate') {
    const mate = mates[mateIndex];
    const last = mateIndex === mates.length - 1;
    body = (
      <MateChapter
        nav={nav}
        mate={mate}
        index={mateIndex}
        total={mates.length}
        answer={draft.chapters[mate.id]}
        context={workedTogether(data, viewer, mate)}
        editable={editable}
        showErrors={errView === mate.id}
        savedText={savedText}
        saveError={store.saveError}
        onChange={setAnswer(mate.id)}
        onSaveBack={() => go(null)}
        nextLabel={last ? 'Next: Retrospective' : 'Next teammate'}
        onNext={(missing) => {
          if (missing) {
            setErrView(mate.id);
            return;
          }
          go(last ? 'retro' : mates[mateIndex + 1].id);
        }}
        onBackToHub={() => go(null)}
      />
    );
  } else if (view === 'retro') {
    body = (
      <RetroChapter
        nav={nav}
        retro={draft.retro}
        editable={editable}
        savedText={savedText}
        saveError={store.saveError}
        onChange={setRetro}
        onSaveBack={() => go(null)}
        onNext={() => go('kit')}
        onBackToHub={() => go(null)}
      />
    );
  } else if (view === 'kit') {
    body = (
      <KitChapter
        nav={nav}
        kit={draft.kit}
        candidates={candidates}
        editable={editable}
        savedText={savedText}
        saveError={store.saveError}
        onChange={setKit}
        onKeep={() => {
          setKit((k) => ({ ...k, decided: true }));
          go(null);
        }}
        onSkip={() => {
          setKit(() => ({ picks: [], decided: true }));
          go(null);
        }}
        onBackToHub={() => go(null)}
      />
    );
  } else {
    body = (
      <HubView
        cp={cp}
        project={data.project}
        mates={mates}
        statusOf={statusOf}
        retroSt={retroSt}
        kitSt={kitSt}
        editable={editable}
        savedText={savedText}
        saveError={store.saveError}
        loadFailed={store.loadFailed}
        onOpen={go}
        onSubmit={() => {
          setSubmitError('');
          setConfirm(true);
        }}
      />
    );
  }

  return (
    <div className='relative flex min-h-screen flex-col bg-bg-twodays text-text-primary'>
      <header className='flex h-14 shrink-0 items-center justify-between gap-3 border-b border-green-deep03/[0.08] bg-grayscale-gray00 px-2 md:h-16 md:px-8'>
        <TdButton
          variant='ghost'
          size='l'
          onClick={inChapter ? () => go(null) : toBoard}
          icon={<BackIcon className='h-4 w-4' />}
          gap='gap-1.5'
          className='h-11 shrink-0 px-2 text-[14px] leading-[140%] text-text-primary hover:bg-transparent hover:text-green-deep04'
        >
          {inChapter ? 'All chapters' : 'Back to workboard'}
        </TdButton>
        <button
          type='button'
          onClick={() => setHelp(true)}
          aria-label='How peer review works'
          title='How peer review works'
          className='flex h-11 w-11 shrink-0 items-center justify-center rounded-full hover:bg-green-deep03/[0.06]'
        >
          <HelpIcon className='h-5 w-5 text-icon-secondary' />
        </button>
      </header>

      {store.loading ? <div className='flex-1' /> : body}

      <Sheet
        open={help}
        onClose={() => setHelp(false)}
        title='How peer review works'
        subtitle='Read this before you start.'
      >
        {HELP.map((h) =>
          h.em ? (
            <div
              key={h.title}
              className='flex flex-col gap-1 rounded-xl border border-green-deep03/[0.12] bg-surface-green10 px-4 py-3'
            >
              <span className='text-[14px] font-medium leading-[140%] text-text-primary'>
                {h.title}
              </span>
              <p className='text-[13px] leading-[150%] text-text-secondary [text-wrap:pretty]'>
                {h.body}
              </p>
            </div>
          ) : (
            <div key={h.title} className='flex flex-col gap-1.5'>
              <span className='text-[14px] font-medium leading-[140%] text-text-primary'>
                {h.title}
              </span>
              <p className='text-[14px] leading-[150%] text-text-secondary [text-wrap:pretty]'>
                {h.body}
              </p>
            </div>
          ),
        )}
      </Sheet>

      <AlertDialog
        open={confirm}
        busy={busy}
        onCancel={() => setConfirm(false)}
        title='Submit peer review?'
        description='After submitting, nothing can be edited.'
        actions={
          <>
            <TdButton variant='soft' size='l' disabled={busy} onClick={() => setConfirm(false)}>
              Cancel
            </TdButton>
            <TdButton size='l' disabled={busy} onClick={doSubmit}>
              {busy ? 'Submitting…' : 'Submit'}
            </TdButton>
          </>
        }
      >
        <div className='grid grid-cols-[auto_minmax(0,1fr)] gap-x-6 gap-y-2 rounded-xl bg-green-deep03/[0.04] px-4 py-3.5 text-[14px] leading-[140%]'>
          {submitSummary({ mates, statusOf, retroSt, picks: draft.kit.picks.length }).map(
            ([l, v]) => (
              <React.Fragment key={l}>
                <span className='text-text-secondary'>{l}</span>
                <span className='font-medium tabular-nums text-text-primary'>{v}</span>
              </React.Fragment>
            ),
          )}
        </div>
        {submitError && <p className='text-[13px] text-text-failed'>{submitError}</p>}
      </AlertDialog>
    </div>
  );
};

export default TwoDaysCompleteProject;
